import React, { useMemo, useCallback, useContext, useState } from 'react';
import { Box } from '@material-ui/core';
import DashboardRoundedIcon from '@material-ui/icons/DashboardRounded';
import DnsRoundedIcon from '@material-ui/icons/DnsRounded';
import FolderRoundedIcon from '@material-ui/icons/FolderRounded';
import NotificationsRoundedIcon from '@material-ui/icons/NotificationsRounded';
import PeopleRoundedIcon from '@material-ui/icons/PeopleRounded';
import AccountBoxRoundedIcon from '@material-ui/icons/AccountBoxRounded';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import Settings from '@material-ui/icons/Settings';
import ReceiptIcon from '@material-ui/icons/Receipt';
import EmailIcon from '@material-ui/icons/Email';
import ResetPasswordIcon from '@material-ui/icons/Lock';
import ConfirmationSupport from '@material-ui/icons/ContactSupport';
import useStyles from './SideMenu.styles';
import { EPaths } from '../../types/enums/EPaths';
import ListItem from './ListItem/SideMenuListItem';
import { ISideMenuItem } from '../../types/interfaces/ISideMenuItem';
import { useTranslation } from 'react-i18next';
import tPaths from '../../utils/tPaths';
import { SessionContext } from '../../contexts/session/SessionContext';
import LocalOffer from '@material-ui/icons/LocalOffer';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import FolderSpecialIcon from '@material-ui/icons/FolderSpecial';

type IMenuItemsExpanded = Record<string, boolean>;

const SideMenu = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { permissions } = useContext(SessionContext);

  const [menuItemsExpanded, setMenuItemsExpanded] = useState<IMenuItemsExpanded>({
    [t(tPaths.sideMenu.notifications)]: false,
  });

  const handleExpandMenuItem = useCallback((label: string) => {
    return () => {
      setMenuItemsExpanded((items: IMenuItemsExpanded) => ({ ...items, [label]: !items[label] }));
    };
  }, []);

  const menuItems: ISideMenuItem[][] = useMemo(
    () => [
      [
        {
          path: EPaths.Dashboard,
          label: t(tPaths.sideMenu.dashboard),
          icon: <DashboardRoundedIcon />,
        },
      ],
      [
        ...(permissions.packages.view
          ? [
              {
                path: EPaths.Packages,
                label: t(tPaths.sideMenu.packages),
                icon: <FolderRoundedIcon />,
              },
            ]
          : []),
      ],
      [
        ...(permissions.promoCampaigns.view
          ? [
              {
                path: EPaths.PromoCampaigns,
                label: t(tPaths.sideMenu.promoCampaigns),
                icon: <FolderSpecialIcon />,
              },
            ]
          : []),
      ],
      [
        ...(permissions.customers.view
          ? [
              {
                path: EPaths.Customers,
                label: t(tPaths.sideMenu.customers),
                icon: <PeopleRoundedIcon />,
              },
            ]
          : []),
        ...(true
          ? [
              {
                path: EPaths.SystemUsers,
                label: t(tPaths.sideMenu.systemUsers),
                icon: <AccountBoxRoundedIcon />,
              },
            ]
          : []),
      ],
      [
        // TODO: uncomment in MVP 2
        // ...(permissions.calculator.view
        //   ? [
        //       {
        //         path: EPaths.Calculator,
        //         label: t(tPaths.sideMenu.calculator),
        //         icon: <IsoRoundedIcon />,
        //       },
        //     ]
        //   : []),
        ...(permissions.carrierStatus.view
          ? [
              {
                path: EPaths.CarrierStatus,
                label: t(tPaths.sideMenu.carrierStatus),
                icon: <DnsRoundedIcon />,
              },
            ]
          : []),
        ...(permissions.notifications.view
          ? [
              {
                label: t(tPaths.sideMenu.notifications),
                icon: <NotificationsRoundedIcon />,
                expanded: menuItemsExpanded[t(tPaths.sideMenu.notifications)],
                onClick: handleExpandMenuItem(t(tPaths.sideMenu.notifications)),
                path: EPaths.Athletes,
                dropdownItems: [
                  {
                    isDropdownItem: true,
                    path: EPaths.Receipt,
                    label: t(tPaths.sideMenu.purchaseReceiptNotification),
                    icon: <ReceiptIcon />,
                  },
                  {
                    isDropdownItem: true,
                    path: EPaths.VerifyEmail,
                    label: t(tPaths.sideMenu.verifyEmailNotification),
                    icon: <EmailIcon />,
                  },
                  {
                    isDropdownItem: true,
                    path: EPaths.ResetPassword,
                    label: t(tPaths.sideMenu.resetPasswordNotification),
                    icon: <ResetPasswordIcon />,
                  },
                  {
                    isDropdownItem: true,
                    path: EPaths.ConfirmationSupport,
                    label: t(tPaths.sideMenu.confirmationSupport),
                    icon: <ConfirmationSupport />,
                  },
                  {
                    isDropdownItem: true,
                    path: EPaths.InAppNotifications,
                    label: t(tPaths.sideMenu.inAppNotifications),
                    icon: <NotificationsActiveIcon />,
                  },
                ],
              },
            ]
          : []),
      ],
      [
        ...(permissions.appSettings.view
          ? [
              {
                path: EPaths.Settings,
                label: t(tPaths.sideMenu.settings),
                icon: <Settings />,
              },
            ]
          : []),
      ],
      [
        ...(permissions.promocodes.view
          ? [
              {
                path: EPaths.PromoCodes,
                label: t(tPaths.sideMenu.promoCodes),
                icon: <VpnKeyIcon />,
              },
            ]
          : []),
        // ...(permissions.partnercodes.view
        //   ? [
        //       {
        //         path: EPaths.PartnerCodes,
        //         label: t(tPaths.sideMenu.partnerCodes),
        //         icon: <LocalOffer />,
        //       },
        //     ]
        //   : []),
      ],
    ],
    [permissions, t, menuItemsExpanded],
  );

  return (
    <Box className={styles.drawer}>
      {menuItems.map((group, index) => (
        <Box key={index} className={styles.group}>
          {group.map((props) => (
            <ListItem {...props} key={props.label} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default SideMenu;
